import React, { useState } from 'react';
import { ArrowRight, Check, Coffee, Gift, Mail, Twitter, DollarSign, CreditCard, Users, Sparkles, AlertCircle, ExternalLink } from 'lucide-react';

interface FlowStep {
  id: number;
  title: string;
  description: string;
  url?: string;
  status: 'pending' | 'testing' | 'complete' | 'error';
  testData?: any;
}

export default function TestingFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([
    {
      id: 1,
      title: '🏠 Landing Page',
      description: 'User arrives at birthday.coffee',
      url: '/',
      status: 'pending',
      testData: {
        url: 'birthday.coffee',
        expected: 'See "Send Birthday Coffee" button',
        actual: null
      }
    },
    {
      id: 2,
      title: '🎨 Choose Animated Card',
      description: 'User selects from 5 animated designs',
      url: '/cards',
      status: 'pending',
      testData: {
        designs: ['Coffee Blast', 'Cake Party', 'Stellar Wishes', 'Heart Warming', 'Golden Hour'],
        selected: null
      }
    },
    {
      id: 3,
      title: '✏️ Customize Message',
      description: 'Enter recipient name, message, sender',
      url: '/cards',
      status: 'pending',
      testData: {
        recipientName: 'Sarah',
        message: 'Hope your birthday is brew-tiful! ☕',
        senderName: 'Alex',
        includesCoffee: true,
        coffeeAmount: 15
      }
    },
    {
      id: 4,
      title: '🎬 Preview Animation',
      description: 'Play card animation with confetti',
      url: '/cards',
      status: 'pending',
      testData: {
        animation: 'confetti',
        duration: '3 seconds',
        effects: ['confetti', 'floating elements', 'pulsing text']
      }
    },
    {
      id: 5,
      title: '💳 Checkout Process',
      description: 'Stripe payment for coffee gift + fee',
      url: '/checkout',
      status: 'pending',
      testData: {
        coffeeAmount: 15.00,
        serviceFee: 2.25,
        total: 17.25,
        paymentMethod: 'Stripe Checkout'
      }
    },
    {
      id: 6,
      title: '🐦 Twitter Share',
      description: 'Auto-post to Twitter with link',
      url: 'twitter.com',
      status: 'pending',
      testData: {
        tweetText: '🎉 Happy Birthday Sarah! 🎂\n\n☕ Enjoy your birthday coffee! Sent with love via @birthdaycoffee',
        cardLink: 'birthday.coffee/card/coffee-blast/Sarah',
        visibility: 'Public tweet'
      }
    },
    {
      id: 7,
      title: '📧 Email Confirmation',
      description: 'Send email to recipient with gift code',
      url: '/admin/email-test',
      status: 'pending',
      testData: {
        to: 'sarah@example.com',
        from: 'gifts@updates.birthday.coffee',
        giftCode: 'COFFEE-XYZ123',
        template: 'birthday-gift'
      }
    },
    {
      id: 8,
      title: '🎉 Success Page',
      description: 'Show confirmation and viral sharing options',
      url: '/success',
      status: 'pending',
      testData: {
        orderId: 'ord_123456',
        shareOptions: ['Twitter', 'Facebook', 'Copy Link'],
        suggestedAction: 'Send another card'
      }
    }
  ]);

  const testStep = (stepId: number) => {
    setFlowSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, status: 'testing' } : step
    ));

    // Simulate testing
    setTimeout(() => {
      setFlowSteps(prev => prev.map(step =>
        step.id === stepId ? { ...step, status: 'complete' } : step
      ));
      if (stepId < flowSteps.length) {
        setCurrentStep(stepId);
      }
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'complete': return 'bg-green-500';
      case 'testing': return 'bg-yellow-500 animate-pulse';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'complete': return <Check className="w-5 h-5 text-white" />;
      case 'testing': return <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-white" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-amber-900 mb-4">
            Birthday Coffee - Complete Testing Flow
          </h1>
          <p className="text-xl text-amber-700">Test the entire user journey from landing to revenue</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <DollarSign className="w-8 h-8 text-green-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">$2.25</p>
            <p className="text-sm text-gray-600">Revenue per $15 gift</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <Users className="w-8 h-8 text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">3-5</p>
            <p className="text-sm text-gray-600">New users per tweet</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <Twitter className="w-8 h-8 text-black mb-2" />
            <p className="text-2xl font-bold text-gray-900">127</p>
            <p className="text-sm text-gray-600">Avg tweet impressions</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <Gift className="w-8 h-8 text-rose-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">98%</p>
            <p className="text-sm text-gray-600">Include coffee gift</p>
          </div>
        </div>

        {/* Main Flow */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-amber-900">User Journey Flow</h2>
            <button
              onClick={() => testStep(1)}
              className="bg-amber-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-amber-700 transition-colors flex items-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              Start Testing Flow
            </button>
          </div>

          {/* Flow Steps */}
          <div className="space-y-6">
            {flowSteps.map((step, index) => (
              <div key={step.id} className="relative">
                <div className={`flex items-start gap-4 p-6 rounded-xl border-2 transition-all ${
                  step.status === 'testing' ? 'border-yellow-400 bg-yellow-50' :
                  step.status === 'complete' ? 'border-green-400 bg-green-50' :
                  'border-gray-200 hover:border-amber-300'
                }`}>
                  {/* Step Number */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${getStatusColor(step.status)}`}>
                    {step.status === 'pending' ? step.id : getStatusIcon(step.status)}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-600 mb-3">{step.description}</p>

                    {/* Test Data */}
                    {step.testData && (
                      <div className="bg-gray-50 rounded-lg p-4 text-sm">
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(step.testData).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-500">{key}:</span>
                              <span className="text-gray-700 font-mono">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {step.url && (
                      <a
                        href={step.url}
                        target={step.url.startsWith('http') ? '_blank' : '_self'}
                        className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors flex items-center gap-1"
                      >
                        Test
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button
                      onClick={() => testStep(step.id)}
                      disabled={step.status === 'complete'}
                      className="bg-amber-100 text-amber-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-200 transition-colors disabled:opacity-50"
                    >
                      {step.status === 'complete' ? 'Completed' : 'Mark Complete'}
                    </button>
                  </div>
                </div>

                {/* Connector Line */}
                {index < flowSteps.length - 1 && (
                  <div className="absolute left-5 top-16 bottom-0 w-0.5 bg-gray-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Test URLs */}
        <div className="mt-12 bg-white rounded-3xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-amber-900 mb-6">Quick Test Links</h2>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-amber-50 rounded-xl p-6">
              <h3 className="font-bold text-amber-900 mb-4">Customer Flow</h3>
              <div className="space-y-2">
                <a href="/" className="flex items-center gap-2 text-amber-700 hover:text-amber-900">
                  <Coffee className="w-4 h-4" />
                  <span>Main Landing Page</span>
                </a>
                <a href="/cards" className="flex items-center gap-2 text-amber-700 hover:text-amber-900">
                  <Gift className="w-4 h-4" />
                  <span>Animated Cards Builder</span>
                </a>
                <a href="/success?test=true" className="flex items-center gap-2 text-amber-700 hover:text-amber-900">
                  <Check className="w-4 h-4" />
                  <span>Success Page (Test Mode)</span>
                </a>
              </div>
            </div>

            <div className="bg-orange-50 rounded-xl p-6">
              <h3 className="font-bold text-amber-900 mb-4">Admin Testing</h3>
              <div className="space-y-2">
                <a href="/admin/email-test" className="flex items-center gap-2 text-orange-700 hover:text-orange-900">
                  <Mail className="w-4 h-4" />
                  <span>Email Testing</span>
                </a>
                <a href="/admin/crud-test" className="flex items-center gap-2 text-orange-700 hover:text-orange-900">
                  <Users className="w-4 h-4" />
                  <span>CRUD Flow Test</span>
                </a>
                <a href="/admin/monetization" className="flex items-center gap-2 text-orange-700 hover:text-orange-900">
                  <DollarSign className="w-4 h-4" />
                  <span>Revenue Dashboard</span>
                </a>
                <a href="/admin/dns" className="flex items-center gap-2 text-orange-700 hover:text-orange-900">
                  <AlertCircle className="w-4 h-4" />
                  <span>DNS Checker</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Calculation */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-600 rounded-3xl shadow-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-6">Revenue Per Transaction</h2>

          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white/20 backdrop-blur rounded-xl p-4">
              <p className="text-amber-100 text-sm mb-1">Gift Amount</p>
              <p className="text-3xl font-bold">$15.00</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-4">
              <p className="text-amber-100 text-sm mb-1">Service Fee (15%)</p>
              <p className="text-3xl font-bold">$2.25</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-4">
              <p className="text-amber-100 text-sm mb-1">Your Revenue</p>
              <p className="text-3xl font-bold text-yellow-300">$2.25</p>
            </div>
          </div>

          <div className="mt-6 bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-amber-100 mb-2">To make your first $100:</p>
            <p className="text-2xl font-bold">45 birthday cards with coffee gifts</p>
            <p className="text-amber-100 text-sm mt-2">That's just 2 cards per day for 3 weeks!</p>
          </div>
        </div>
      </div>
    </div>
  );
}